import { useCallback, useEffect, useMemo, useState } from 'react';
import * as settings from '../api/settings';
import type { SettingsCompany, SettingsUser } from '../api/settings';
import { CreateCompanyModal } from './CreateCompanyModal';
import { SettingsToast } from './SettingsToast';

interface EditingRename {
  teamId: string;
  displayName: string;
  color: string;
}

/**
 * Companies management tab. Lists every company (super-admin only) as a
 * card with rename/delete/add-member controls.
 *
 * Member chips: `listCompanies()` only returns `memberCount`, not the
 * member subs. Displaying per-company member chips would require an N+1
 * API call or a new endpoint — we intentionally don't do that here.
 * Instead the card shows member COUNT + an "Add member" picker that lets
 * the admin ship the common workflow (adding a user to a company). The
 * user list is fetched once so the picker can render names.
 */
export function CompaniesTab() {
  const [companies, setCompanies] = useState<SettingsCompany[]>([]);
  const [users, setUsers] = useState<SettingsUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addingMemberFor, setAddingMemberFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<EditingRename | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, us] = await Promise.all([
        settings.listCompanies(),
        settings.listUsers(false),
      ]);
      setCompanies(cs);
      setUsers(us);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sorted = useMemo(
    () => [...companies].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [companies]
  );

  const onDelete = useCallback(
    async (c: SettingsCompany) => {
      if (!window.confirm(`Delete company "${c.displayName}"? This cannot be undone.`)) return;
      setBusyId(c.teamId);
      try {
        await settings.deleteCompany(c.teamId);
        setToast(`Deleted ${c.displayName}`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const onAddMember = useCallback(
    async (teamId: string, sub: string) => {
      setBusyId(teamId);
      try {
        await settings.addCompanyMember(teamId, sub);
        const user = users.find((u) => u.userId === sub);
        setToast(`Added ${user?.displayName ?? 'user'}`);
        setAddingMemberFor(null);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh, users]
  );

  const onSaveRename = useCallback(async () => {
    if (!renaming) return;
    const { teamId, displayName, color } = renaming;
    const name = displayName.trim();
    if (!name) return;
    setBusyId(teamId);
    try {
      await settings.updateCompany(teamId, { displayName: name, color });
      setToast(`Renamed to ${name}`);
      setRenaming(null);
      await refresh();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }, [renaming, refresh]);

  return (
    <div className="settings-tab-body">
      <div className="settings-tab-toolbar">
        <span className="muted small">
          {companies.length} {companies.length === 1 ? 'company' : 'companies'}
        </span>
        <button
          type="button"
          className="primary"
          onClick={() => setCreateOpen(true)}
        >
          + New company
        </button>
      </div>

      {loading ? (
        <div className="settings-loading muted">Loading companies…</div>
      ) : error ? (
        <div className="settings-error">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="settings-empty muted">
          No companies yet — create your first.
        </div>
      ) : (
        <div className="settings-company-grid">
          {sorted.map((c) => {
            const memberCount = c.memberCount ?? 0;
            const canDelete = memberCount === 0;
            const busy = busyId === c.teamId;
            const isRenaming = renaming?.teamId === c.teamId;
            const isAddingMember = addingMemberFor === c.teamId;

            return (
              <div key={c.teamId} className="settings-company-card">
                <div className="settings-company-card-head">
                  {isRenaming ? (
                    <div className="settings-company-rename">
                      <input
                        type="color"
                        value={renaming.color}
                        onChange={(e) =>
                          setRenaming({ ...renaming, color: e.target.value })
                        }
                      />
                      <input
                        type="text"
                        value={renaming.displayName}
                        onChange={(e) =>
                          setRenaming({ ...renaming, displayName: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void onSaveRename();
                          if (e.key === 'Escape') setRenaming(null);
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <span
                        className="settings-color-dot settings-color-dot-lg"
                        style={{ backgroundColor: c.color }}
                        aria-hidden="true"
                      />
                      <div className="settings-company-name">
                        <strong>{c.displayName}</strong>
                        <span className="muted small">{c.teamId}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="settings-company-meta">
                  <span className="settings-chip">
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                  </span>
                </div>

                {isAddingMember && (
                  <div className="settings-add-member">
                    <span className="settings-field-label">Add member</span>
                    <div className="settings-chip-row">
                      {users.length === 0 ? (
                        <span className="muted small">No users available.</span>
                      ) : (
                        users.map((u) => (
                          <button
                            key={u.userId}
                            type="button"
                            className="settings-chip-button"
                            disabled={busy}
                            onClick={() => void onAddMember(c.teamId, u.userId)}
                          >
                            <span
                              className="settings-color-dot"
                              style={{ backgroundColor: u.color }}
                              aria-hidden="true"
                            />
                            {u.displayName}
                          </button>
                        ))
                      )}
                    </div>
                    <button
                      type="button"
                      className="ghost small"
                      onClick={() => setAddingMemberFor(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                <div className="settings-company-actions">
                  {isRenaming ? (
                    <>
                      <button
                        type="button"
                        className="primary"
                        disabled={busy}
                        onClick={() => void onSaveRename()}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setRenaming(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setRenaming({
                            teamId: c.teamId,
                            displayName: c.displayName,
                            color: c.color,
                          })
                        }
                      >
                        Rename
                      </button>
                      {!isAddingMember && (
                        <button
                          type="button"
                          onClick={() => setAddingMemberFor(c.teamId)}
                        >
                          + Add member
                        </button>
                      )}
                      <button
                        type="button"
                        className="danger"
                        disabled={busy || !canDelete}
                        title={
                          !canDelete
                            ? 'Remove all members first'
                            : undefined
                        }
                        onClick={() => void onDelete(c)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateCompanyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={async (msg) => {
          setCreateOpen(false);
          setToast(msg);
          await refresh();
        }}
      />
      <SettingsToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
